import * as IPurchases from '../../../../../interfaces/merchants/products/purchases';
import * as IInputs from '../../../../../interfaces/merchants/products/purchases/inputs';

import * as P from '../../../../reducers/merchants/products/purchases';
import * as I from '../../../../reducers/merchants/products/purchases/inputs';
import * as a from './api';
import securedAxios from '../../../../../utils/secured-axios';

import { MerchantDetail } from '../../../../../interfaces/merchants';
import { buildApiV2Url } from 'src/utils/request-url';

class PurchasingError implements Error {
  public name = 'PurchasingError';

  constructor(public message: string) {}

  public toString() {
    return this.name + ': ' + this.message;
  }
}

export class RestartError extends PurchasingError {
  public name = 'RestartError';
}

export class ReturnTargetStepError extends PurchasingError {
  public name = 'ReturnTargetStepError';
}

export const inputPurchasingConfirmation =
  (
    schemeType: IPurchases.SchemeType,
    productId: string,
    inputs: IInputs.State,
  ) =>
  async (dispatch: I.Dispatch & P.Dispatch) => {
    dispatch({ payload: { postable: false }, type: P.Types.SetPostable });
    const res = await a.postPurchasingFinish(schemeType, productId, inputs);
    if (!res.success && res.errors && res.errors.customer_custom_fields) {
      const customFields = await a.fetchCustomFields(productId);
      dispatch({
        payload: {
          customFields,
          errors: res.errors!.customer_custom_fields,
        },
        type: P.Types.SetCustomFieldsAndErrors,
      });
      dispatch({
        payload: {
          errors: [IPurchases.EntireErrorType.CustomerFailed],
        },
        type: P.Types.SetEntireErrors,
      });
      dispatch({ payload: { postable: true }, type: P.Types.SetPostable });
      throw new ReturnTargetStepError(
        'post api failed : customer_custom_fields',
      );
    }
    if (!res.success && res.errors && res.errors.payment_method) {
      dispatch({
        payload: {
          errors: [IPurchases.EntireErrorType.PaymentFailed],
        },
        type: P.Types.SetEntireErrors,
      });
      dispatch({ payload: { postable: true }, type: P.Types.SetPostable });
      throw new ReturnTargetStepError('post api failed : payment_method');
    }
    // 無効なカードブランドでの支払いの場合
    if (!res.success && res.errors && res.errors.jcb_brand_disabled) {
      dispatch({
        payload: {
          errors: [IPurchases.EntireErrorType.JCBBrandDisabled],
        },
        type: P.Types.SetEntireErrors,
      });
      dispatch({ payload: { postable: true }, type: P.Types.SetPostable });
      throw new ReturnTargetStepError('post api failed : jcb_brand_disabled');
    }
    if (!res.success) {
      dispatch({
        payload: {
          errors: [IPurchases.EntireErrorType.Unexpected],
        },
        type: P.Types.SetEntireErrors,
      });
      dispatch({ payload: { postable: true }, type: P.Types.SetPostable });
      throw new RestartError('post api failed : unexpected');
    }
    dispatch({
      payload: { verifiedUser: !!res.verified_user, customer: res.customer },
      type: P.Types.SetPurchasedUser,
    });
    dispatch({
      payload: { lastOrder: res.last_order },
      type: P.Types.SetLastOrder,
    });
    dispatch({ payload: { postable: true }, type: P.Types.SetPostable });
  };

export const inputPurchasingPayment =
  (
    schemeType: IPurchases.SchemeType,
    productId: string,
    payment: IInputs.Payment,
    merchantPublicId: string,
  ) =>
  async (dispatch: I.Dispatch & P.Dispatch) => {
    dispatch({ payload: { postable: false }, type: P.Types.SetPostable });
    const merchant = await securedAxios.get<MerchantDetail>(
      buildApiV2Url(`/merchants/${merchantPublicId}`),
    );
    const stripeResponse = await a.postStripe(
      payment,
      merchant.data.jcb_enabled,
    );
    if (stripeResponse && stripeResponse.error) {
      dispatch({ payload: { postable: true }, type: P.Types.SetPostable });
      dispatch({
        payload: { errors: stripeResponse.error! },
        type: P.Types.SetStripeErrors,
      });
      throw new Error('postPayment failed');
    }
    const res = await a.postPurchasingPayment(schemeType, productId, {
      payment,
    });
    dispatch({ payload: { postable: true }, type: P.Types.SetPostable });
    if (!res.success) {
      /*
    dispatch({
      payload: {
        finishedStatus: IReserve.FinishedStatus.Failed,
        entireError: [IReserve.BookingFailedReason.Unexpected],
      },
      type: B.Types.SetFinishedStatus,
    });
    */
      throw new Error('post api failed');
    }
    const token = stripeResponse && stripeResponse.token;
    dispatch({
      payload: { payment: { ...payment, token } },
      type: I.Types.InputPayment,
    });
  };

const fieldCheck = (
  userInfo: IInputs.UserInfo,
  errors: IPurchases.PurchasingErrors,
): boolean => {
  const fieldIds = Object.keys(userInfo.customerFields!);
  const errorFieldIds = Object.keys(errors.customer_custom_fields);
  return errorFieldIds.every((id) => fieldIds.includes(id));
};

export const inputPurchasingUserInfo =
  (
    schemeType: IPurchases.SchemeType,
    productId: string,
    userInfo: IInputs.UserInfo,
  ) =>
  async (dispatch: I.Dispatch & P.Dispatch) => {
    dispatch({ payload: { postable: false }, type: P.Types.SetPostable });
    const res = await a.postPurchasingCustomer(schemeType, productId, {
      userInfo,
    });
    dispatch({ payload: { postable: true }, type: P.Types.SetPostable });
    if (!res.success) {
      if (fieldCheck(userInfo, res.errors!)) {
        dispatch({
          payload: {
            errors: res.errors!.customer_custom_fields,
          },
          type: P.Types.SetCustomerErrors,
        });
      } else {
        const customFields = await a.fetchCustomFields(productId);
        dispatch({
          payload: {
            customFields,
            errors: res.errors!.customer_custom_fields,
          },
          type: P.Types.SetCustomFieldsAndErrors,
        });
      }
      throw new Error('post api failed');
    }
    dispatch({
      payload: { userInfo },
      type: I.Types.InputUserInfo,
    });
  };

export const clearCustomerError = (name: string) => (dispatch: P.Dispatch) => {
  if (name.indexOf('customerFields') !== -1) {
    const id = Number(name.replace('customerFields.', ''));
    dispatch({ payload: { id }, type: P.Types.ClearCustomerError });
  }
};
